<template>
  <div class="class-entry-form bg-gray-50 p-4 rounded-lg border border-gray-200">
    <h3 class="font-medium text-gray-800 mb-4">
      <div class="flex items-center">
        <span class="material-symbols-outlined mr-2">{{ isEditing ? 'edit' : 'add' }}</span>
        {{ isEditing ? 'Update Lecture' : 'Add New Lecture' }}
      </div>
    </h3>
    
    <form @submit.prevent="submitClass">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Module Code Dropdown (no custom module option) -->
        <div>
          <label for="moduleCode" class="block text-sm font-medium text-gray-700">Module*</label>
          <div class="relative">
            <select
              id="moduleCode"
              v-model="form.moduleCode"
              required
              class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
              @change="handleModuleChange"
            >
              <option value="" disabled>Select a module</option>
              <option 
                v-for="module in userModules" 
                :key="module" 
                :value="module">
                {{ module }}
              </option>
              <option value="__add_new__">Add new module...</option>
            </select>
          </div>
          <p v-if="moduleCodeError" class="text-xs text-red-600 mt-1">{{ moduleCodeError }}</p>
          <p v-else class="text-xs text-gray-500 mt-1">Select a module from your registered list</p>
          <div v-if="userModules.length === 0" class="text-xs text-blue-700 mt-2 flex items-center">
            <span class="material-symbols-outlined text-xs mr-1">info</span>
            No modules found. <button type="button" @click="goToAddModule" class="underline text-blue-800 ml-1">Add modules</button> in your profile.
          </div>
        </div>

        <!-- Day of Week -->
        <div>
          <label for="day" class="block text-sm font-medium text-gray-700">Day of Week*</label>
          <select
            id="day"
            v-model="form.day"
            required
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
          >
            <option value="" disabled>Select day</option>
            <option v-for="day in daysOfWeek" :key="day" :value="day">{{ day }}</option>
          </select>
        </div>

        <!-- Start Time -->
        <div>
          <label for="startTime" class="block text-sm font-medium text-gray-700">Start Time*</label>
          <input
            type="time"
            id="startTime"
            v-model="form.startTime"
            required
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
          />
        </div>

        <!-- End Time -->
        <div>
          <label for="endTime" class="block text-sm font-medium text-gray-700">End Time*</label>
          <input
            type="time"
            id="endTime"
            v-model="form.endTime"
            required
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
          />
          <p v-if="timeError" class="mt-1 text-xs text-red-600">{{ timeError }}</p>
        </div>

        <!-- Venue -->
        <div>
          <label for="venue" class="block text-sm font-medium text-gray-700">Venue*</label>
          <input
            type="text"
            id="venue"
            v-model="form.venue"
            required
            placeholder="e.g. IT Building 2-24"
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
          />
        </div>

        <!-- Type -->
        <div>
          <label for="type" class="block text-sm font-medium text-gray-700">Type*</label>
          <select
            id="type"
            v-model="form.type"
            required
            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
          >
            <option value="" disabled>Select class type</option>
            <option v-for="type in classTypes" :key="type" :value="type">{{ type }}</option>
          </select>
        </div>
      </div>

      <!-- Additional Notes -->
      <div class="mt-4">
        <label for="notes" class="block text-sm font-medium text-gray-700">Notes (Optional)</label>
        <textarea
          id="notes"
          v-model="form.notes"
          rows="2"
          placeholder="Any additional information about this class"
          class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-900 focus:border-blue-900 sm:text-sm"
        ></textarea>
      </div>

      <!-- Form Actions -->
      <div class="mt-6 flex justify-end space-x-3">
        <button
          type="button"
          @click="cancelEdit"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          :disabled="isSubmitting || !!timeError || !!moduleCodeError"
          class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-900 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          <span v-if="isSubmitting">
            <span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
            Processing...
          </span>
          <span v-else>
            {{ isEditing ? 'Update Lecture' : 'Add Lecture' }}
          </span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  props: {
    editClass: {
      type: Object,
      default: null
    },
    userModules: {
      type: Array,
      default: () => []
    }
  },
  emits: ['class-added', 'class-updated', 'cancel-edit', 'add-module'],
  data() {
    return {
      form: {
        moduleCode: '',
        day: '',
        startTime: '',
        endTime: '',
        venue: '',
        type: '',
        notes: ''
      },
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      classTypes: ['Lecture', 'Practical', 'Tutorial', 'Other'],
      isSubmitting: false,
      timeError: '',
      moduleCodeError: ''
    };
  },
  computed: {
    ...mapState(['profile']),
    isEditing() {
      return Boolean(this.editClass);
    }
  },
  watch: {
    editClass: {
      handler(newValue) {
        if (newValue) {
          this.form = { ...newValue };
        } else {
          this.resetForm();
        }
      },
      immediate: true
    },
    'form.startTime': function() {
      this.validateTimes();
    },
    'form.endTime': function() {
      this.validateTimes();
    },
    'form.moduleCode': function(newVal) {
      this.validateModuleCode();
      // Log the selected module to the console
      console.log('[ClassEntryForm] Selected module:', newVal);
    }
  },
  methods: {
    validateModuleCode() {
      if (!this.form.moduleCode) {
        this.moduleCodeError = 'Module code must be selected';
      } else {
        this.moduleCodeError = '';
      }
    },
    validateTimes() {
      if (this.form.startTime && this.form.endTime) {
        const start = new Date(`2000-01-01T${this.form.startTime}`);
        const end = new Date(`2000-01-01T${this.form.endTime}`);
        if (start >= end) {
          this.timeError = 'End time must be after start time';
        } else {
          this.timeError = '';
        }
      } else {
        this.timeError = '';
      }
    },
    goToAddModule() {
      this.$emit('add-module');
    },
    handleModuleChange() {
      if (this.form.moduleCode === '__add_new__') {
        this.form.moduleCode = '';
        this.goToAddModule();
      }
    },
    async submitClass() {
      this.validateModuleCode();
      if (this.moduleCodeError) {
        return;
      }
      this.validateTimes();
      if (this.timeError) {
        return;
      }
      this.isSubmitting = true;
      
      try {
        // Create a clean class data object with all required fields
        const classData = {
          moduleCode: this.form.moduleCode,
          day: this.form.day,
          startTime: this.form.startTime,
          endTime: this.form.endTime,
          venue: this.form.venue,
          type: this.form.type,
          notes: this.form.notes?.trim() || ''
        };
        
        if (this.isEditing) {
          // Include the ID for updates
          classData.id = this.editClass.id;
          this.$emit('class-updated', classData);
        } else {
          this.$emit('class-added', classData);
        }
        
        // Only reset the form when not editing or when successfully added/updated
        if (!this.isEditing) {
          this.resetForm();
        }
      } catch (error) {
        console.error('Error submitting class:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    cancelEdit() {
      this.resetForm();
      this.$emit('cancel-edit');
    },
    resetForm() {
      this.form = {
        moduleCode: '',
        day: '',
        startTime: '',
        endTime: '',
        venue: '',
        type: '',
        notes: ''
      };
      this.timeError = '';
      this.moduleCodeError = '';
      this.isSubmitting = false;
    }
  }
};
</script>

